import React from 'react';
import { createContext } from 'use-context-selector';
import { useAuth } from '@auth-wrapper';
import {
  convertConsumptionHistory as convertConsumptionHistoryAPI,
  type ConvertConsumptionHistoryParams,
  ConsumptionTypeEnum,
} from '@services/scenarios-simulator';
import { notifyError } from '@components/atoms/toast/services';
import { transformObjectKeysToCamelCase } from '@utils/common';
import { IConsumptionInfo } from '@schemas/scenarios-simulator/types';

export { ConsumptionTypeEnum };
export type { ConvertConsumptionHistoryParams as IConvertConsumptionHistoryParms };

interface ConvertedConsumptionHistoryContextData {
  convertedConsumptionHistory: Record<string, any>;
  isLoadingConvertedConsumptionHistory: boolean;
  convertConsumptionHistory: (params: ConvertConsumptionHistoryParams) => Promise<IConsumptionInfo | Error>;
}

export const ConvertedConsumptionHistoryContext = createContext({} as ConvertedConsumptionHistoryContextData);

interface Provider {
  children: React.ReactNode;
}

const ConvertedConsumptionHistoryProvider: React.FC<Provider> = ({ children }) => {
  const {
    authStatus: { accessToken: token },
  } = useAuth();

  const [convertedConsumptionHistory, setConvertedConsumptionHistory] = React.useState<Record<string, any>>({});
  const [isLoadingConvertedConsumptionHistory, setIsLoadingConvertedConsumptionHistory] =
    React.useState<boolean>(false);

  const convertConsumptionHistory = React.useCallback(
    async (params: ConvertConsumptionHistoryParams): Promise<IConsumptionInfo | Error> => {
      if (!token) {
        notifyError('No authentication token available');
        return new Error('No authentication token available');
      }

      setIsLoadingConvertedConsumptionHistory(true);
      try {
        const result = await convertConsumptionHistoryAPI(params, token);
        setConvertedConsumptionHistory(transformObjectKeysToCamelCase(result));
        return result;
      } finally {
        setIsLoadingConvertedConsumptionHistory(false);
      }
    },
    [token],
  );

  return (
    <ConvertedConsumptionHistoryContext.Provider
      value={{
        convertedConsumptionHistory,
        isLoadingConvertedConsumptionHistory,
        convertConsumptionHistory,
      }}
    >
      {children}
    </ConvertedConsumptionHistoryContext.Provider>
  );
};

export default ConvertedConsumptionHistoryProvider;
